/**
 * CaptureModal Component
 * 
 * Modal for capturing content via text or file upload.
 * Supports drag-and-drop file upload and displays inline processing status.
 * 
 * Features:
 * - Text ingestion via dedicated form
 * - File upload with drag-and-drop
 * - Inline processing status
 * - Poll status endpoint every 5 seconds during processing
 * 
 * Requirements: 14.2, 14.3, 14.6, 14.7
 */

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { VoiceButton } from '@/components/chat/VoiceButton';
import { VoiceCapture } from '@/components/capture/VoiceCapture';
import { cn } from '@/lib/utils';
import * as ingestAPI from '@/api/ingest';
import * as knowledgeAPI from '@/api/knowledge';
import { queryKeys } from '@/lib/queryKeys';
import { useChatStore } from '@/store/chatStore';
import { useUIStore } from '@/store/uiStore';
import type { BulkIngestItem, IngestTextResponse, IngestURLResponse, NotesReviewResponse, Task, WSFrame } from '@/types/api';

const MAX_BULK_URLS = 100;
const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi;

type QueueStatus = 'processing' | 'ready' | 'failed' | 'duplicate';

interface QueueRow {
  item_id?: string;
  url: string;
  status: QueueStatus;
  status_stage?: string;
  progress_message?: string;
  error?: string;
  existing_item_id?: string;
}

const terminalStatuses = new Set<QueueStatus>(['ready', 'failed', 'duplicate']);

export interface CaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CaptureModal({ open, onOpenChange }: CaptureModalProps) {
  const queryClient = useQueryClient();
  const currentSessionId = useChatStore((state) => state.currentSessionId);
  const captureInitialTab = useUIStore((state) => state.captureInitialTab);
  const captureVoiceRequestId = useUIStore((state) => state.captureVoiceRequestId);
  const [activeTab, setActiveTab] = React.useState<'text' | 'file' | 'url' | 'notes' | 'voice'>('notes');

  React.useEffect(() => {
    if (open) setActiveTab(captureInitialTab);
  }, [captureInitialTab, open]);

  // Text input state
  const [textInput, setTextInput] = React.useState('');
  const [urlInput, setUrlInput] = React.useState('');
  const [urlError, setUrlError] = React.useState<string | null>(null);
  const [notesInput, setNotesInput] = React.useState('');
  const [notesFile, setNotesFile] = React.useState<File | null>(null);
  const [notesError, setNotesError] = React.useState<string | null>(null);

  // File upload state
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const notesFileInputRef = React.useRef<HTMLInputElement>(null);

  // Processing state
  const [processingItemId, setProcessingItemId] = React.useState<string | null>(null);
  const [extractedTasks, setExtractedTasks] = React.useState<Task[]>([]);
  const [queueRows, setQueueRows] = React.useState<QueueRow[]>(() => {
    return queryClient.getQueryData<QueueRow[]>(['ingestQueue']) || [];
  });
  const [queuePaused, setQueuePaused] = React.useState(false);

  // Text ingestion mutation
  const textMutation = useMutation({
    mutationFn: (text: string) => ingestAPI.text(text, 'paste'),
    onSuccess: (data: IngestTextResponse) => {
      // Invalidate knowledge queries
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
      
      // Show extracted tasks if any
      if (data.tasks_extracted && data.tasks_extracted.length > 0) {
        setExtractedTasks(data.tasks_extracted);
        // Keep modal open to show extracted tasks
        setTimeout(() => {
          setExtractedTasks([]);
          setTextInput('');
          onOpenChange(false);
        }, 5000);
      } else {
        // Show success and close modal
        setTextInput('');
        onOpenChange(false);
      }
    },
    onError: (error: Error) => {
      console.error('Text ingestion error:', error);
    },
  });

  // File ingestion mutation
  const fileMutation = useMutation({
    mutationFn: (file: File) => ingestAPI.file(file),
    onSuccess: (data: IngestURLResponse) => {
      // Start polling for status
      setProcessingItemId(data.item_id);
      setSelectedFile(null);
    },
    onError: (error: Error) => {
      console.error('File ingestion error:', error);
      setSelectedFile(null);
    },
  });

  const notesReviewMutation = useMutation({
    mutationFn: () => ingestAPI.notesReview({
      text: notesInput,
      file: notesFile || undefined,
      chatSessionId: currentSessionId,
    }),
    onSuccess: (data: NotesReviewResponse) => {
      setNotesInput('');
      setNotesFile(null);
      setNotesError(null);
      onOpenChange(false);
      window.location.hash = '#/';
      window.dispatchEvent(new CustomEvent('notes-review-ready', {
        detail: { sessionId: data.session_id },
      }));
    },
    onError: (error: Error) => {
      setNotesError(error.message || 'Could not start notes review');
    },
  });

  const normalizeUrl = React.useCallback((url: string) => url.trim().replace(/[.,)]$/, ''), []);

  const parseUrls = React.useCallback((text: string): string[] => {
    const matches = text.match(URL_REGEX) || [];
    const seen = new Set<string>();
    return matches
      .map(normalizeUrl)
      .filter((url) => {
        const key = url.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [normalizeUrl]);

  const upsertQueueRows = React.useCallback((rows: QueueRow[]) => {
    setQueueRows((current) => {
      const byIdOrUrl = new Map<string, QueueRow>();
      current.forEach((row) => {
        byIdOrUrl.set(row.item_id || row.url, row);
      });
      rows.forEach((row) => {
        byIdOrUrl.set(row.item_id || row.url, {
          ...(byIdOrUrl.get(row.item_id || row.url) || {}),
          ...row,
        });
      });
      const nextRows = Array.from(byIdOrUrl.values());
      queryClient.setQueryData(['ingestQueue'], nextRows);
      return nextRows;
    });
  }, [queryClient]);

  const bulkUrlMutation = useMutation({
    mutationFn: (urls: string[]) => ingestAPI.bulkUrls(urls),
    onSuccess: (data) => {
      const toQueueRow = (item: BulkIngestItem): QueueRow => ({
        item_id: item.item_id,
        url: item.url,
        status: item.status,
        status_stage: item.status_stage,
        progress_message: item.message || (item.status === 'duplicate' ? 'Already saved' : undefined),
        error: item.error,
        existing_item_id: item.existing_item_id,
      });
      upsertQueueRows([
        ...data.items.map(toQueueRow),
        ...data.duplicates.map((item) => toQueueRow({ ...item, status: 'duplicate', status_stage: 'duplicate' })),
        ...data.rejected.map((item) => toQueueRow({ ...item, status: 'failed', status_stage: 'error' })),
      ]);
      setUrlInput('');
      setUrlError(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
    },
    onError: (error: Error) => {
      setUrlError(error.message || 'Failed to queue URLs');
    },
  });

  const { data: reelQueueState } = useQuery({
    queryKey: ['reelQueueState'],
    queryFn: () => ingestAPI.getReelQueueState(),
    enabled: open && queueRows.length > 0,
    refetchInterval: queueRows.some((row) => row.status === 'processing') ? 10000 : false,
  });

  React.useEffect(() => {
    if (reelQueueState) {
      setQueuePaused(reelQueueState.paused);
    }
  }, [reelQueueState]);

  const pauseQueueMutation = useMutation({
    mutationFn: () => ingestAPI.pauseReelQueue(),
    onSuccess: (state) => {
      setQueuePaused(state.paused);
      queryClient.setQueryData(['reelQueueState'], state);
    },
  });

  const resumeQueueMutation = useMutation({
    mutationFn: () => ingestAPI.resumeReelQueue(),
    onSuccess: (state) => {
      setQueuePaused(state.paused);
      queryClient.setQueryData(['reelQueueState'], state);
    },
  });

  // Poll status endpoint every 5 seconds during processing
  const { data: statusData } = useQuery({
    queryKey: ['itemStatus', processingItemId],
    queryFn: () => knowledgeAPI.getStatus(processingItemId!),
    enabled: !!processingItemId,
    refetchInterval: (query) => {
      // Stop polling when status is 'ready' or 'failed'
      if (query.state.data?.status === 'ready' || query.state.data?.status === 'failed') {
        return false;
      }
      return 5000; // Poll every 5 seconds
    },
  });

  // Handle status completion
  React.useEffect(() => {
    if (statusData?.status === 'ready') {
      // Invalidate knowledge queries
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
      
      // Close modal after success
      setTimeout(() => {
        setProcessingItemId(null);
        onOpenChange(false);
      }, 1500);
    } else if (statusData?.status === 'failed') {
      // Clear processing state on failure
      setTimeout(() => {
        setProcessingItemId(null);
      }, 3000);
    }
  }, [statusData, queryClient, onOpenChange]);

  React.useEffect(() => {
    if (queueRows.length === 0) return;
    queryClient.setQueryData(['ingestQueue'], queueRows);
    if (queueRows.every((row) => terminalStatuses.has(row.status))) {
      const timeoutId = window.setTimeout(() => {
        setQueueRows([]);
        queryClient.removeQueries({ queryKey: ['ingestQueue'] });
      }, 5 * 60 * 1000);
      return () => window.clearTimeout(timeoutId);
    }
  }, [queueRows, queryClient]);

  React.useEffect(() => {
    if (queueRows.length === 0) return;
    const wsBase = (import.meta.env.VITE_WS_BASE_URL as string | undefined)?.replace(/\/$/, '');
    const wsUrl = wsBase
      ? `${wsBase}/ws/chat`
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/chat`;
    const websocket = new WebSocket(wsUrl);

    websocket.onmessage = (event) => {
      try {
        const frame = JSON.parse(event.data) as WSFrame;
        if (frame.type === 'knowledge_status' && frame.item_id) {
          upsertQueueRows([{
            item_id: frame.item_id,
            url: queueRows.find((row) => row.item_id === frame.item_id)?.url || '',
            status: frame.status || 'processing',
            status_stage: frame.status_stage,
            progress_message: frame.progress_message,
            error: frame.error_code,
          }]);
        } else if (frame.type === 'knowledge_status_batch' && frame.item_ids?.length) {
          upsertQueueRows(frame.item_ids.map((itemId) => ({
            item_id: itemId,
            url: queueRows.find((row) => row.item_id === itemId)?.url || '',
            status: frame.status || 'processing',
            status_stage: frame.status_stage,
            progress_message: frame.progress_message,
            error: frame.error_code,
          })));
        } else if (frame.type === 'reel_queue_status') {
          setQueuePaused(Boolean(frame.paused));
          queryClient.setQueryData(['reelQueueState'], {
            paused: Boolean(frame.paused),
            queued_count: frame.queued_count || 0,
          });
        }
      } catch (error) {
        console.error('Failed to parse queue websocket frame:', error);
      }
    };

    websocket.onopen = () => {
      websocket.send(JSON.stringify({ type: 'ping' }));
    };

    return () => websocket.close();
  }, [queueRows, queryClient, upsertQueueRows]);

  // Handle text submission
  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (textInput.trim()) {
      textMutation.mutate(textInput.trim());
    }
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const urls = parseUrls(urlInput);
    if (urls.length === 0) {
      setUrlError('Paste at least one Instagram Reel URL.');
      return;
    }
    if (urls.length > MAX_BULK_URLS) {
      setUrlError(`Paste ${MAX_BULK_URLS} URLs or fewer at a time.`);
      return;
    }
    const unsupported = urls.find((url) => !url.includes('instagram.com'));
    if (unsupported) {
      setUrlError('Bulk capture currently supports Instagram/Reel URLs only.');
      return;
    }
    setUrlError(null);
    bulkUrlMutation.mutate(urls);
  };

  const handleRetryQueueRow = (row: QueueRow) => {
    if (!row.item_id) return;
    upsertQueueRows([{ ...row, status: 'processing', status_stage: 'queued', progress_message: 'Waiting in queue...', error: undefined }]);
    knowledgeAPI.retryItem(row.item_id)
      .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all }))
      .catch((error: Error) => {
        upsertQueueRows([{ ...row, status: 'failed', status_stage: 'error', error: error.message }]);
      });
  };

  const handlePauseQueue = () => {
    pauseQueueMutation.mutate();
  };

  const handleResumeQueue = () => {
    resumeQueueMutation.mutate();
  };

  // Handle voice transcript change
  const handleVoiceTranscriptChange = (transcript: string) => {
    setTextInput(transcript);
  };

  // Handle voice final transcript
  const handleVoiceFinalTranscript = (transcript: string) => {
    setTextInput(transcript);
    // Auto-submit after voice input completes
    if (transcript.trim()) {
      setTimeout(() => {
        textMutation.mutate(transcript.trim());
      }, 500);
    }
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleNotesFileSelect = (file: File) => {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.md', '.txt'].includes(extension)) {
      setNotesError('Notes review supports .md and .txt files. PDFs are not supported yet.');
      setNotesFile(null);
      return;
    }
    setNotesError(null);
    setNotesFile(file);
  };

  const handleNotesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!notesInput.trim() && !notesFile) {
      setNotesError('Paste notes or attach a .md/.txt file.');
      return;
    }
    setNotesError(null);
    notesReviewMutation.mutate();
  };

  // Handle file upload
  const handleFileUpload = () => {
    if (selectedFile) {
      fileMutation.mutate(selectedFile);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (activeTab === 'notes') {
        handleNotesFileSelect(files[0]);
      } else {
        handleFileSelect(files[0]);
      }
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (activeTab === 'notes') {
        handleNotesFileSelect(files[0]);
      } else {
        handleFileSelect(files[0]);
      }
    }
  };

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setTextInput('');
      setUrlInput('');
      setUrlError(null);
      setNotesInput('');
      setNotesFile(null);
      setNotesError(null);
      setSelectedFile(null);
      setProcessingItemId(null);
      setExtractedTasks([]);
      setActiveTab('notes');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Capture Content</DialogTitle>
          <DialogDescription>
            Add text notes or upload files to your knowledge base
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'text' | 'file' | 'url' | 'notes' | 'voice')}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="text">Text</TabsTrigger>
            <TabsTrigger value="url">Reel URLs</TabsTrigger>
            <TabsTrigger value="file">Files</TabsTrigger>
            <TabsTrigger value="voice">Voice</TabsTrigger>
          </TabsList>

          <TabsContent value="voice" className="space-y-4">
            <VoiceCapture autoStartKey={captureVoiceRequestId} onComplete={() => onOpenChange(false)} />
          </TabsContent>

          <TabsContent value="notes" className="space-y-4">
            <form onSubmit={handleNotesSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="notes-input" className="text-sm font-medium text-text-secondary">
                  Notes for review
                </label>
                <textarea
                  id="notes-input"
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  placeholder="Paste messy notes here, or attach a .md/.txt file below..."
                  rows={7}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                  disabled={notesReviewMutation.isPending}
                />
              </div>

              <div
                className={cn(
                  'relative rounded-lg border-2 border-dashed p-5 text-center transition-colors',
                  isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/50',
                  notesReviewMutation.isPending && 'pointer-events-none opacity-50'
                )}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  ref={notesFileInputRef}
                  type="file"
                  onChange={handleFileInputChange}
                  className="sr-only"
                  id="notes-file-upload"
                  accept=".md,.txt,text/markdown,text/plain"
                />

                {!notesFile ? (
                  <div className="space-y-3">
                    <p className="text-sm text-text-secondary">
                      Drop a markdown or text file here
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => notesFileInputRef.current?.click()}
                      className="mt-1"
                    >
                      browse .md/.txt files
                    </Button>
                    <p className="text-xs text-text-muted">
                      PDFs are not part of notes review yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-text-primary">
                      {notesFile.name}
                    </p>
                    <p className="text-xs text-text-muted">
                      {(notesFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setNotesFile(null)}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              {(notesError || notesReviewMutation.isError) && (
                <div className="rounded-lg border border-error/20 bg-error/10 px-3 py-2 text-sm text-error">
                  {notesError || notesReviewMutation.error?.message || 'Could not start notes review'}
                </div>
              )}

              {notesReviewMutation.isPending && (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Preparing review...</span>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={notesReviewMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={(!notesInput.trim() && !notesFile) || notesReviewMutation.isPending}
                >
                  Start Review
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Text Tab */}
          <TabsContent value="text" className="space-y-4">
            <form onSubmit={handleTextSubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="text-input" className="text-sm font-medium text-text-secondary">
                    Text content
                  </label>
                  <VoiceButton
                    onTranscriptChange={handleVoiceTranscriptChange}
                    onFinalTranscript={handleVoiceFinalTranscript}
                    size="sm"
                  />
                </div>
                <textarea
                  id="text-input"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Paste or type your text here, or use voice input..."
                  rows={8}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                  disabled={textMutation.isPending}
                />
              </div>

              {/* Text ingestion status */}
              {textMutation.isPending && (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Processing text...</span>
                </div>
              )}

              {textMutation.isError && (
                <div className="flex items-center gap-2 text-sm text-error">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="8" y2="12" />
                    <line x1="12" x2="12.01" y1="16" y2="16" />
                  </svg>
                  <span>{textMutation.error?.message || 'Failed to process text'}</span>
                </div>
              )}

              {textMutation.isSuccess && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>Text captured successfully!</span>
                  </div>

                  {/* Display extracted tasks */}
                  {extractedTasks.length > 0 && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
                      <p className="mb-2 text-sm font-medium text-green-800 dark:text-green-200">
                        Extracted {extractedTasks.length} task{extractedTasks.length > 1 ? 's' : ''}:
                      </p>
                      <ul className="space-y-1">
                        {extractedTasks.map((task, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-green-700 dark:text-green-300">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="mt-0.5 h-3 w-3 flex-shrink-0"
                            >
                              <polyline points="9 11 12 14 22 4" />
                              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                            <span>{task.title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={textMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!textInput.trim() || textMutation.isPending}
                >
                  Capture Text
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="url" className="space-y-4">
            <form onSubmit={handleUrlSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="url-input" className="text-sm font-medium text-text-secondary">
                  Instagram Reel URLs
                </label>
                <textarea
                  id="url-input"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="Paste one or many Instagram Reel links..."
                  rows={7}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                  disabled={bulkUrlMutation.isPending}
                />
                <p className="text-xs text-text-muted">
                  Apify resolves the links together. Downloads and analysis run locally in a queue.
                </p>
              </div>

              {(urlError || bulkUrlMutation.isError) && (
                <div className="rounded-lg border border-error/20 bg-error/10 px-3 py-2 text-sm text-error">
                  {urlError || bulkUrlMutation.error?.message || 'Failed to queue URLs'}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={bulkUrlMutation.isPending}
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  disabled={!urlInput.trim() || bulkUrlMutation.isPending}
                >
                  {bulkUrlMutation.isPending ? 'Queueing...' : 'Queue Reels'}
                </Button>
              </div>
            </form>

            {queueRows.length > 0 && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-2 text-xs text-text-secondary">
                    {(['processing', 'ready', 'failed', 'duplicate'] as QueueStatus[]).map((status) => {
                      const count = queueRows.filter((row) => row.status === status).length;
                      if (!count) return null;
                      return (
                        <span key={status} className="rounded bg-surface-container px-2 py-1">
                          {count} {status}
                        </span>
                      );
                    })}
                    {queuePaused && (
                      <span className="rounded bg-warning/10 px-2 py-1 text-warning">
                        paused
                      </span>
                    )}
                  </div>
                  {queueRows.some((row) => row.status === 'processing') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={queuePaused ? handleResumeQueue : handlePauseQueue}
                      disabled={pauseQueueMutation.isPending || resumeQueueMutation.isPending}
                    >
                      {queuePaused ? 'Resume queue' : 'Pause queue'}
                    </Button>
                  )}
                </div>

                <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                  {queueRows.map((row) => (
                    <div
                      key={row.item_id || row.url}
                      className="rounded-lg border border-outline-variant/20 bg-surface-container-low px-3 py-2"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-text-primary">
                            {row.url || row.item_id}
                          </p>
                          <p className="text-xs text-text-muted">
                            {row.progress_message || row.status_stage || row.status}
                          </p>
                          {row.error && (
                            <p className="text-xs text-error">
                              {row.error}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className={cn(
                            'rounded px-2 py-1 text-[10px] font-medium uppercase',
                            row.status === 'ready' && 'bg-success/10 text-success',
                            row.status === 'failed' && 'bg-error/10 text-error',
                            row.status === 'duplicate' && 'bg-warning/10 text-warning',
                            row.status === 'processing' && 'bg-primary/10 text-primary'
                          )}>
                            {row.status_stage || row.status}
                          </span>
                          {row.status === 'failed' && row.item_id && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRetryQueueRow(row)}
                            >
                              Retry
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* File Upload Tab */}
          <TabsContent value="file" className="space-y-4">
            <div
              className={cn(
                'relative rounded-lg border-2 border-dashed p-8 text-center transition-colors',
                isDragging
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/50',
                (fileMutation.isPending || processingItemId) && 'pointer-events-none opacity-50'
              )}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                className="sr-only"
                id="file-upload"
                accept="image/*,audio/*,video/*,.pdf,.md,.markdown,.txt"
              />

              {!selectedFile ? (
                <div className="space-y-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mx-auto h-12 w-12 text-gray-400"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" x2="12" y1="3" y2="15" />
                  </svg>
                  <div>
                    <p className="text-sm text-text-secondary">
                      Drag and drop a file here, or
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-1"
                    >
                      browse files
                    </Button>
                  </div>
                  <p className="text-xs text-text-muted">
                    Supports audio, video, PDF, and text files
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mx-auto h-12 w-12 text-green-500"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" x2="8" y1="13" y2="13" />
                    <line x1="16" x2="8" y1="17" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-text-muted">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>

            {/* File upload status */}
            {fileMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-primary">
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Uploading file...</span>
              </div>
            )}

            {processingItemId && statusData && (
              <div className="space-y-2">
                {statusData.status === 'processing' && (
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <svg
                      className="h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>{statusData.progress_message || 'Processing file...'}</span>
                  </div>
                )}

                {statusData.status === 'ready' && (
                  <div className="flex items-center gap-2 text-sm text-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>File processed successfully!</span>
                  </div>
                )}

                {statusData.status === 'failed' && (
                  <div className="flex items-center gap-2 text-sm text-error">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" x2="12" y1="8" y2="12" />
                      <line x1="12" x2="12.01" y1="16" y2="16" />
                    </svg>
                    <span>{statusData.error || 'Failed to process file'}</span>
                  </div>
                )}
              </div>
            )}

            {fileMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-error">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" x2="12" y1="8" y2="12" />
                  <line x1="12" x2="12.01" y1="16" y2="16" />
                </svg>
                <span>{fileMutation.error?.message || 'Failed to upload file'}</span>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={fileMutation.isPending || !!processingItemId}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleFileUpload}
                disabled={!selectedFile || fileMutation.isPending || !!processingItemId}
              >
                Upload File
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
